import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import PasswordInput from "@/components/PasswordInput";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(token ? "" : "Missing reset token. Request a new link from the sign-in page.");

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return;
    if (password.length < 8) {
      return toast.error("Password must be at least 8 characters");
    }
    if (password !== confirm) {
      return toast.error("Passwords do not match");
    }
    setLoading(true);
    setError("");
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(
        formatApiError(err.response?.data?.detail) || "This reset link is invalid or has expired."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-slate-50 px-4">
      <div className="w-full max-w-md space-y-6">
        <Link to="/" className="inline-flex items-center gap-2">
          <img src="/logo.png" alt="Jamoora" className="h-20 w-20 rounded-xl object-contain" />
          <span className="font-display font-bold text-lg">Jamoora</span>
        </Link>

        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
          {done ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
              <h1 className="font-display text-2xl font-bold">Password updated</h1>
              <p className="text-slate-600 text-sm">You can now sign in with your new password.</p>
              <Button
                className="w-full bg-blue-600 hover:bg-blue-700"
                onClick={() => navigate("/login")}
                data-testid="reset-continue"
              >
                Sign in
              </Button>
            </div>
          ) : error && !token ? (
            <div className="text-center space-y-4" data-testid="reset-error">
              <XCircle className="h-12 w-12 text-red-500 mx-auto" />
              <h1 className="font-display text-2xl font-bold">Invalid link</h1>
              <p className="text-slate-600 text-sm">{error}</p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/forgot-password">Request a new link</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <h1 className="font-display text-2xl font-bold">Choose a new password</h1>
                <p className="text-slate-600 text-sm mt-1">Enter a new password for your account.</p>
              </div>
              {error && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 text-rose-800 text-sm p-3" data-testid="reset-error">
                  {error}
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider">New password</Label>
                <PasswordInput
                  required
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="reset-password-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs font-medium uppercase tracking-wider">Confirm password</Label>
                <PasswordInput
                  required
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-password-confirm"
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !token}
                className="w-full bg-blue-600 hover:bg-blue-700"
                data-testid="reset-submit"
              >
                {loading ? "Updating…" : "Update password"}
              </Button>
              <div className="text-center">
                <Link to="/login" className="text-sm text-slate-500 hover:text-slate-900">
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
